import { useCallback, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isPlainObject from 'lodash/isPlainObject';
import sum from 'lodash/sum';

import { ENV } from 'services/environment/environment';

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const deepCopy = <T>(object: object | Array<object>): T => cloneDeep(object) as T;

export const capitaliseFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const currentEnvironmentIndicatorColor = {
  local: '#8B5CF6',
  dev: '#06B6D4',
  staging: '#F59E0B',
  production: '#000000',
}[ENV.BACKEND_ENV];

export const isProduction = () => {
  return ENV.BACKEND_ENV === 'production';
};

export const isStaging = () => {
  return ENV.BACKEND_ENV === 'staging';
};

export const isDevelopment = () => {
  return ENV.BACKEND_ENV === 'dev';
};

export const isLocal = () => {
  return ENV.BACKEND_ENV === 'local';
};

export const humanizeString = (title = ''): string => {
  return title
    .toLowerCase()
    .split('_')
    .map((x: string) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(' ');
};

export const isDefined = <T>(value: T): value is NonNullable<T> => {
  return value !== null && value !== undefined;
};

export const withoutNulls = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(withoutNulls);
  }

  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, value]) => typeof value !== 'undefined' && value !== null)
        .map(([key, value]) => [key, withoutNulls(value)]),
    );
  }

  return obj;
};

export const countTrueValues = (obj: Record<string, any>): number => {
  // Using flatMap to recursively traverse the object and count true values
  return sum(
    flatMap(obj, (value) => {
      if (isBoolean(value)) {
        return value ? 1 : 0;
      }
      if (isPlainObject(value)) {
        return countTrueValues(value);
      }
      if (isArray(value)) {
        return sum(value.map((item) => (isPlainObject(item) ? countTrueValues(item) : 0)));
      }
      return 0;
    }),
  );
};

export const useIntervalSinceMount = () => {
  const initialTime = useRef(Date.now());
  const getIntervalSinceMount = useCallback(() => Date.now() - initialTime.current, []);

  return getIntervalSinceMount;
};

/**
 * Get the initials from a string.
 * @param text string to extract initials from
 * @param maxNumberOfInitials maximum number of initials to return. If 0, return all initials.
 * @returns the extracted initials
 *  @example
 * // Returns 'JD'
 * getInitials('John Doe');
 *
 * // Returns 'J'
 * getInitials('John Doe', 1);
 *
 * // Returns 'JDS'
 * getInitials('John Doe Smith', 0);
 */
export const getInitials = (text: string, maxNumberOfInitials = 2): string => {
  const initials = text
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();

  if (!maxNumberOfInitials) {
    return initials;
  }
  return initials.slice(0, maxNumberOfInitials);
};
